import { defineStore } from 'pinia';
import { ICatalog, ICatalogAllLike } from '@/storePinia/stores/catalog/interfaces/ICatalog';
import axios from 'axios';
import {
  ICatalogSmartFilterItemCheckbox,
  ICatalogSmartFilterItemPrice,
} from '@/storePinia/stores/catalog/interfaces/ICatalogSmartFilter';
import useLoader from '@use/useLoader/useLoader';
import router from '@/routs/router';
import { useResultsStore } from '@/storePinia/stores/results';
import { IBreadcrumbs } from '@/interfaces';
import { ISiteLanguages } from '@/storePinia/stores/results/ISiteLanguages';
import { usePersonalStore } from '@/storePinia/stores/personal';

const loader = useLoader();

type ICatalogGetters = {
  getSelectedFilters: (
    state: ICatalog
  ) => Array<ICatalogSmartFilterItemCheckbox | ICatalogSmartFilterItemPrice>;
  clearUrlParam: (state: ICatalog) => (payload: { url: string; param: string }) => string;
};

type IChangePageDataProps = {
  url: string;
};

type ICatalogActions = {
  changeFilter: () => Promise<void>;
  changePageData: (payload: string | IChangePageDataProps) => Promise<void>;
  getMoreItems: () => Promise<ICatalog | undefined>;
  addUserCatalogFilter: (payload: { filterName: string }) => Promise<
    | {
        response: {
          status: boolean;
          text: string;
        };
      }
    | undefined
  >;
  deleteSelectedItem: (payload: { CODE: string; CONTROL_ID: string; priceRange?: boolean }) => void;
};

export const useCatalogStore = defineStore<'catalog', ICatalog, ICatalogGetters, ICatalogActions>(
  'catalog',
  {
    state: (): ICatalog => ({
      allLikes: {},
      catalogSectionList: {},
      catalogSmartFilter: {},
      catalogSort: {},
      collection: {},
      isOpenFilter: false,
      productListCardType: 1,
    }),
    getters: {
      getSelectedFilters: (state) => {
        const items = state.catalogSmartFilter?.ITEMS;
        const arr: Array<ICatalogSmartFilterItemCheckbox | ICatalogSmartFilterItemPrice> = [];

        if (items) {
          Object.values(items).forEach((item) => {
            const subObj = item.VALUES;

            if (item.DISPLAY_TYPE === 'A') {
              const range: Record<string, string> = {};
              const newObj = {
                ...item,
                CODE: item.CODE,
                printRange: '',
              };

              Object.entries(subObj).forEach(([i, value]) => {
                if (value.HTML_VALUE) {
                  range[i] = value.HTML_VALUE;
                }
              });

              if (range.MIN && range.MAX) {
                newObj.printRange = `от ${range.MIN} до ${range.MAX}`;
              } else if (range.MIN) {
                newObj.printRange = `от ${range.MIN}`;
              } else if (range.MAX) {
                newObj.printRange = `до ${range.MAX}`;
              }

              if (newObj.printRange) {
                arr.push(newObj);
              }
            } else if (subObj) {
              Object.values(subObj).forEach((value) => {
                if (value.CHECKED) {
                  arr.push({
                    ...value,
                    CODE: item.CODE,
                  });
                }
              });
            }
          });
        }

        return arr;
      },
      clearUrlParam: () => (payload) => {
        const url = payload.url.split('?');
        if (url?.[1]) {
          return url
            .map((el, ind) => {
              switch (ind) {
                case 1: {
                  const searchParams = new URLSearchParams(`?${el}`);
                  searchParams.delete(payload.param);
                  return searchParams.toString();
                }
                default:
                  return el;
              }
            })
            .join('?');
        }
        return payload.url;
      },
    },
    actions: {
      deleteSelectedItem(payload) {
        try {
          const ITEMS: ICatalog['catalogSmartFilter']['ITEMS'] = JSON.parse(
            JSON.stringify(this.catalogSmartFilter.ITEMS)
          );

          const newItems = ITEMS?.map((el) => {
            if (el.CODE === payload.CODE) {
              if (el.DISPLAY_TYPE !== 'A') {
                /** изменяем состояние обычного checkbox * */

                Object.values(el.VALUES).forEach((item) => {
                  if (item.CONTROL_ID === payload.CONTROL_ID) {
                    // eslint-disable-next-line no-param-reassign
                    item.CHECKED = false;
                  }
                });
              } else {
                /** изменяем состояние range price * */
                const min = el.VALUES?.MIN;
                if (min && Object.prototype.hasOwnProperty.call(min, 'HTML_VALUE')) {
                  delete (min as { HTML_VALUE?: string }).HTML_VALUE;
                }

                const max = el.VALUES?.MAX;
                if (max && Object.prototype.hasOwnProperty.call(max, 'HTML_VALUE')) {
                  delete (max as { HTML_VALUE?: string }).HTML_VALUE;
                }
              }
            } else if (payload.priceRange) {
              // remove price range
              (el as ICatalogSmartFilterItemPrice)?.priceRange?.VALUES.forEach((item) => {
                if (item.CONTROL_ID === payload.CONTROL_ID) {
                  // eslint-disable-next-line no-param-reassign
                  item.CHECKED = false;
                }
              });
            }

            return el;
          });

          this.$patch({ catalogSmartFilter: { ...this.catalogSmartFilter, ITEMS: newItems } });
        } catch (err) {
          console.error(err);
        }
      },
      /**
       * change catalog filter
       * * */
      async changeFilter() {
        try {
          loader.start();
          const form: HTMLFormElement | null = window.document.querySelector('.filter');
          if (form) {
            const formData = new FormData(form);

            /** create query string from filter form * */
            const formEntries = [...formData.entries()];
            const asString = formEntries
              .map(
                ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
              )
              .join('&');

            if (!formData) throw new Error('form is undefined');

            /** бек формирует url на основании отправленной формы * */
            const response = await axios.get(`${window.location.pathname}?${asString}`);
            const { SEF_SET_FILTER_URL, FILTER_URL } = response.data;
            const url = SEF_SET_FILTER_URL || FILTER_URL;

            /** делаем запрос по сформированном url и и обновляем стору * */
            if (url) {
              this.changePageData(url);
            } else {
              throw new Error(`${response} url не сформовано`);
            }
          }
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },

      async changePageData(payload) {
        const resultsStore = useResultsStore();
        let url: string;

        if (typeof payload === 'string') {
          url = payload;
        } else {
          url = payload.url;
        }

        /** using crutches, be careful */
        if (url) {
          url = this.clearUrlParam({ url, param: 'api' });
        }

        try {
          loader.start();

          interface IResponse {
            siteLanguages?: ISiteLanguages;
            breadcrumbs?: IBreadcrumbs;
            allLikes?: ICatalogAllLike;
          }

          const response: { status: number; data: IResponse } = await axios.get(url, {
            params: { api: 'y' },
          });

          const { siteLanguages, breadcrumbs, allLikes } = response.data;

          if (siteLanguages) {
            resultsStore.$patch({ siteLanguages });
            delete response.data.siteLanguages;
          }
          if (breadcrumbs) {
            resultsStore.$patch({ breadcrumbs });
            delete response.data.breadcrumbs;
          }
          if (allLikes) {
            resultsStore.$patch({
              allLikes: {
                ...resultsStore.allLikes,
                ...allLikes,
              },
            });
          }

          if (response.status === 200) {
            // commit('catalog/setState', response.data);

            this.$patch(response.data);

            router.push(url);
          }
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },

      async getMoreItems() {
        try {
          if (this.catalogSectionList.pagination) {
            loader.start();
            const { pagingKey, pageNumber } = this.catalogSectionList.pagination;

            if (pagingKey && pageNumber) {
              const config = {
                params: {
                  api: 'y',
                  [pagingKey]: +pageNumber + 1,
                },
              };

              const {
                status,
                data: response,
              }: {
                status: number;
                data: ICatalog;
              } = await axios.get(window.location.href, config);

              const copyResponse = JSON.parse(JSON.stringify(response));

              if (status === 200) {
                this.$patch({
                  ...copyResponse,
                  catalogSectionList: {
                    ...response.catalogSectionList,
                    items: [
                      ...(this.catalogSectionList.items ?? []),
                      ...(response?.catalogSectionList?.items ?? []),
                    ],
                  },
                });

                if (response?.allLikes) {
                  const resultsStore = useResultsStore();
                  resultsStore.$patch({
                    allLikes: {
                      ...resultsStore.allLikes,
                      ...response?.allLikes,
                    },
                  });
                }
              }
              return response;
            }
          }
          return undefined;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },

      async addUserCatalogFilter(payload) {
        try {
          loader.start();
          if (this.catalogSmartFilter.routes?.addUserCatalogFilter) {
            const personalStore = usePersonalStore();
            const {
              path,
              'params-example-data': { action },
            } = this.catalogSmartFilter.routes.addUserCatalogFilter;

            const userId = personalStore.authInfo?.user_id;
            const savedFilters = this.catalogSmartFilter?.savedFilters;

            const requestData = {
              action,
              userId,
              catalogUrl: savedFilters?.catalogUrl,
              filterUrl: savedFilters?.catalogFilterUrl,
              filterValue: savedFilters?.filterValue,
              ...payload,
            };

            const {
              data,
            }: {
              data: {
                response: {
                  status: boolean;
                  text: string;
                };
              };
            } = await axios.post(path, requestData);

            if (data.response.status === true && typeof savedFilters?.filterUrl === 'string') {
              await this.changePageData(savedFilters?.filterUrl);
            }

            return data;
          }
          return undefined;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
    },
  }
);
